import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Turtle, History, ChevronLeft, ChevronRight, Filter,
  CheckCircle, XCircle, Clock, MessageSquare, Lightbulb, LogIn
} from 'lucide-react';
import api from '../services/api';

interface GameRecord {
  _id: string;
  storyTitle: string;
  userAnswer: string;
  mode: string;
  correct: boolean;
  questionsAsked: number;
  hintsUsed: number;
  duration: number;
  createdAt: string;
}

interface Pagination {
  current: number;
  pages: number;
  total: number;
}

const modeLabels: Record<string, string> = {
  single: '海龟汤推理',
  dual: '双线叙事',
  mentor: '导师故事',
  scenario: '情景模拟',
};

const GameHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState<GameRecord[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ current: 1, pages: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoggedIn(false);
      setLoading(false);
      return;
    }
    setIsLoggedIn(true);
    fetchHistory(page);
  }, [page]);

  const fetchHistory = async (p: number) => {
    setLoading(true);
    try {
      const res = await api.get('/user/history', { params: { page: p, limit: 15 } });
      if (res.data.success) {
        setRecords(res.data.data || []);
        setPagination(res.data.pagination || { current: 1, pages: 0, total: 0 });
      }
    } catch (err) {
      if ((err as any)?.response?.status === 401) {
        localStorage.removeItem('token');
        setIsLoggedIn(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}秒`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s > 0 ? `${m}分${s}秒` : `${m}分钟`;
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-turtle-50 to-white">
        <nav className="glass sticky top-0 z-50 border-b border-gray-200">
          <div className="container mx-auto px-4 py-4 flex items-center">
            <button onClick={() => navigate('/')} className="flex items-center space-x-2">
              <Turtle className="w-6 h-6 text-ocean-600" />
              <span className="text-lg font-bold text-ocean-600">法底捞针</span>
            </button>
          </div>
        </nav>
        <div className="container mx-auto px-4 py-20 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <LogIn className="w-16 h-16 text-ocean-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">请先登录</h2>
            <p className="text-gray-500 mb-6">登录后查看你的游戏历史</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-ocean-600 text-white rounded-xl hover:bg-ocean-700 transition-colors"
            >
              返回首页
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-turtle-50 to-white">
      {/* 导航栏 */}
      <nav className="glass sticky top-0 z-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/dashboard')} className="flex items-center space-x-2">
              <ChevronLeft className="w-5 h-5 text-ocean-600" />
              <Turtle className="w-6 h-6 text-ocean-600" />
              <span className="text-lg font-bold text-ocean-600">游戏历史</span>
            </button>
            <span className="text-sm text-gray-400">
              共 {pagination.total} 条记录
            </span>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* 加载中 */}
        {loading && (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ocean-600" />
          </div>
        )}

        {/* 无记录 */}
        {!loading && records.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <History className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">还没有游戏记录</h3>
            <p className="text-gray-400 mb-6">去玩一局海龟汤推理吧!</p>
            <button
              onClick={() => navigate('/stories')}
              className="px-6 py-3 bg-ocean-600 text-white rounded-xl hover:bg-ocean-700 transition-colors"
            >
              开始推理
            </button>
          </motion.div>
        )}

        {/* 记录列表 */}
        {!loading && records.length > 0 && (
          <AnimatePresence>
            <div className="space-y-4">
              {records.map((record, index) => (
                <motion.div
                  key={record._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          record.correct ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {record.correct ? (
                            <CheckCircle className="w-3 h-3 mr-1" />
                          ) : (
                            <XCircle className="w-3 h-3 mr-1" />
                          )}
                          {record.correct ? '破解成功' : '未能破解'}
                        </span>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                          {modeLabels[record.mode] || record.mode}
                        </span>
                      </div>
                      <h4 className="text-base font-semibold text-gray-800">{record.storyTitle}</h4>
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">
                      {new Date(record.createdAt).toLocaleString('zh-CN', {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                  </div>

                  {/* 统计信息行 */}
                  <div className="flex gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <MessageSquare className="w-4 h-4" />
                      提问 {record.questionsAsked} 次
                    </span>
                    <span className="flex items-center gap-1">
                      <Lightbulb className="w-4 h-4" />
                      提示 {record.hintsUsed} 次
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatDuration(record.duration)}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </AnimatePresence>
        )}

        {/* 分页 */}
        {pagination.pages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <span className="text-sm text-gray-500">
              {pagination.current} / {pagination.pages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
              disabled={page >= pagination.pages}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameHistoryPage;
